// SVG/crop constants — must match body-diagram.tsx
const CROP_X = 425;
const CROP_Y = 157;
const CROP_W = 154;
const CROP_H = 154;
const SVG_W = 595.28;
const SVG_H = 841.89;

/**
 * Convert full-SVG percentage coords (as used in TRAUMA_CROSS_POSITIONS /
 * PDF_ZONE_POSITIONS in body-diagram.tsx) to crop-relative percentages.
 */
function svgPctToCropPct(xPct: number, yPct: number) {
  return {
    x: ((xPct / 100) * SVG_W - CROP_X) / CROP_W * 100,
    y: ((yPct / 100) * SVG_H - CROP_Y) / CROP_H * 100,
  };
}

// ─── Trauma X-mark markers ───────────────────────────────────────────────────

export interface BodyInjuryMarker {
  id: string;
  /** PDF-point x coordinate (bottom-left origin) — used for pixel detection */
  pdfX: number;
  /** PDF-point y coordinate (bottom-left origin) — used for pixel detection */
  pdfY: number;
  /** Sampling radius in pixels at 2x scale (default 6) */
  radius: number;
  /** Crop-relative x% to store on the case (centered on the body part) */
  canonicalXPct: number;
  /** Crop-relative y% to store on the case (centered on the body part) */
  canonicalYPct: number;
}

function marker(
  id: string,
  pdfX: number,
  pdfY: number,
  canonicalSvgXPct: number,
  canonicalSvgYPct: number,
  radius = 6,
): BodyInjuryMarker {
  const { x, y } = svgPctToCropPct(canonicalSvgXPct, canonicalSvgYPct);
  return { id, pdfX, pdfY, radius, canonicalXPct: x, canonicalYPct: y };
}

/**
 * Predefined body-part trauma marker positions on form-001/о PDF page 1.
 * - pdfX/pdfY: where to sample for an X mark (PDF points, bottom-left origin)
 * - canonical*: centered position in the body diagram (from TRAUMA_CROSS_POSITIONS /
 *   PDF_ZONE_POSITIONS in body-diagram.tsx), used when storing the injury mark.
 *
 * Canonical positions reference (full-SVG %, from TRAUMA_CROSS_POSITIONS):
 *   See body-diagram.tsx for the full list of canonical positions.
 */
export const BODY_INJURY_MARKERS: BodyInjuryMarker[] = [
  // Head
  marker("front-head",           462, 675,  77.74, 19.79, 2),
  marker("back-head",            536, 675,  90.51, 19.79, 2),
  // Chest / upper back
  marker("front-chest",          465, 647,  77.67, 23.31),
  marker("back-chest",           539, 647,  90.39, 23.26),
  // Abdomen / lower back
  marker("front-upperAbdomen",   465, 634,  77.69, 24.66),
  marker("front-abdomen",        464, 622,  77.75, 26.11),
  marker("back-upperAbdomen",    537, 632,  90.52, 26.11),
  // Hands
  marker("front-rightHand",      437, 603,  72.92, 28.00),
  marker("front-leftHand",       494, 602,  82.69, 28.00),
  marker("back-rightHand",       567, 603,  95.46, 27.98),
  marker("back-leftHand",        510, 602,  85.72, 27.98),
  // Forearms
  marker("front-rightForearm",   440, 619,  73.60, 26.44),
  marker("front-leftForearm",    489, 620,  82.02, 26.44),
  marker("back-rightForearm",    562, 621,  94.79, 26.44),
  marker("back-leftForearm",     516, 619,  86.39, 26.44),
  // Upper arms
  marker("front-rightUpperArm",  444, 643,  74.94, 23.33),
  marker("front-leftUpperArm",   486, 642,  80.67, 23.33),
  marker("back-rightUpperArm",   558, 644,  93.44, 23.32),
  marker("back-leftUpperArm",    518, 642,  87.74, 23.32),
  // Shins / calves
  marker("front-rightShin",      453, 565,  76.18, 31.80),
  marker("front-leftShin",       478, 565,  79.04, 31.80),
  marker("back-rightCalf",       551, 565,  91.81, 31.80),
  marker("back-leftCalf",        527, 565,  88.95, 31.81),
  // Thighs
  marker("front-rightThigh",     455, 600,  76.18, 29.06),
  marker("front-leftThigh",      475, 600,  79.04, 29.06),
  marker("back-rightThigh",      549, 600,  91.81, 29.07),
  marker("back-leftThigh",       528, 600,  88.95, 29.06),
  // Feet
  marker("front-rightFoot",      448, 543,  76.18, 35.48),
  marker("front-leftFoot",       480, 542,  79.04, 35.48),
  marker("back-rightFoot",       555, 542,  91.81, 35.49),
  marker("back-leftFoot",        522, 541,  88.95, 35.48),
];

/** Field key used to pass detected injury marks through the extracted-fields record. */
export const BODY_INJURY_MARKS_KEY = "p1-bodyInjuryMarks";

// ─── Burn zone markers ───────────────────────────────────────────────────────

export interface BurnZoneMarker {
  /** Zone ID matching PDF_ZONE_POSITIONS in body-diagram.tsx */
  zoneId: string;
  /** Polygon vertices in PDF points (bottom-left origin). Wound clockwise or CCW — doesn't matter. */
  points: [number, number][];
  /** Override the global BURN_RED_THRESHOLD for this zone (optional). */
  threshold?: number;
}

/** Rectangle helper — converts x1/y1/x2/y2 to a 4-point polygon (order-independent). */
function rect(zoneId: string, x1: number, y1: number, x2: number, y2: number): BurnZoneMarker {
  const [lx, rx] = [Math.min(x1, x2), Math.max(x1, x2)];
  const [by, ty] = [Math.min(y1, y2), Math.max(y1, y2)];
  return { zoneId, points: [[lx, by], [rx, by], [rx, ty], [lx, ty]] };
}

/** Explicit polygon helper — supply vertices in PDF points (bottom-left origin). */
function poly(zoneId: string, ...pts: [number, number][]): BurnZoneMarker {
  return { zoneId, points: pts };
}

/**
 * Polygon sampling regions for burn detection on form-001/о PDF page 1.
 * Each region covers the corresponding body part in the diagram.
 * Red pixel coverage above BURN_RED_THRESHOLD triggers a burn for that zone.
 *
 * Coordinates are PDF points (bottom-left origin). Add more zones as needed.
 */
export const BURN_ZONE_MARKERS: BurnZoneMarker[] = [
  poly("front-head",      [473, 663], [459, 663], [456, 687], [474, 688]),
  poly("front-chest",     [447, 660], [483, 660], [478, 637], [452, 636]),
  rect("front-abdomen",   452, 618, 478, 637),

  { ...rect("front-genitalia", 463, 610, 467, 614), threshold: 0.5 },
  poly("front-rightLeg",  [464, 606], [449, 618], [440, 534], [458, 535]),
  poly("front-leftLeg",   [466, 605], [481, 620], [490, 534], [473, 535]),
  poly("front-rightArm",  [450, 640], [444, 663], [424, 599], [441, 591]),
  poly("front-leftArm",   [480, 643], [485, 664], [502, 595], [487, 595]),
  // Back figure
  poly("back-head",       [546, 663], [532, 663], [529, 687], [547, 688]),
  poly("back-upperBack",  [520, 660], [556, 660], [551, 637], [525, 636]),
  poly("back-lowerBack",  [525, 637], [551, 637], [551, 611], [526, 611]),
  poly("back-rightLeg",   [537, 606], [521, 608], [513, 534], [531, 535]),
  poly("back-leftLeg",    [539, 605], [554, 608], [563, 534], [546, 535]),
  poly("back-rightArm",   [523, 640], [517, 663], [497, 599], [514, 591]),
  poly("back-leftArm",    [553, 643], [558, 664], [575, 595], [560, 595]),
];

/**
 * Fraction of sampled pixels that must be red-dominant to detect a burn.
 * Kept low — burns drawn with 2×2 pixel blocks don't need to fill the region.
 */
export const BURN_RED_THRESHOLD = 0.04;

/** Field key used to pass detected burn zones through the extracted-fields record. */
export const BURN_ZONES_KEY = "p1-burnZones";
